import { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import dayjs from 'dayjs'
import apReportService from '@api/v1/apReportService'
import { specializedAttachFormats } from '@api/v1/specializedReportService'
import reportJobService from '@api/v1/reportJobService'
import SpecializedReportShell from '@components/reports/SpecializedReportShell'
import SpecializedFilterBar from '@components/reports/SpecializedFilterBar'
import { objectField, dateField } from '@components/reports/specializedFilterFields'
import ReportViewsMenu from '@components/reports/ReportViewsMenu'
import useSavedReportView from '@components/reports/useSavedReportView'
import { describeFilters, dateRangePart } from '@components/reports/reportFilterSummary'
import ReportDataGrid from '@components/reports/ReportDataGrid'
import { withAliasKeys } from '@components/reports/aliasedGridColumns'
import ReportGroupedGrid from '@components/reports/ReportGroupedGrid'
import { useReportExportView } from '@components/reports/reportExportViewContext'
import { exportFileName } from '@components/reports/reportGridUtils'
import { fmtDisplayDate } from '@utils/displayDate'
import useLatestRun from '@hooks/useLatestRun'

const TYPE_VENDORS = 55 // CBObjectTypes.VENDORS

// Module-level so useLatestRun's `run` keeps one identity (spec 131).
const runErrorMessage = (err) => err.message || 'Report failed'

const fmtMoney = (v) => `$${Number(v ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
// Himaad UI feedback, 2026-08-27: A/P's local unpadded 'M/D/YYYY' copy is gone —
// the transaction and due dates below come from `fmtDisplayDate` (MM/DD/YYYY,
// src/utils/displayDate.js), the one Report Center display formatter. Legitimate
// here because A/P SYNTHESIZES its own dataCells from CB's raw JSON, unlike the
// 119 generic reports whose cells CB pre-formats server-side. The old copy echoed
// an unparseable value back; the shared formatter returns '' instead, on purpose —
// on a vendor-payables statement a blank is visibly missing information while a
// foreign-shaped date invites the reader to interpret it (displayDate.js).
// `AsOfDate` in the wire object below stays ISO.

// displayValue / groupHeaderContent are injected as HTML by the universal
// grids (dangerouslySetInnerHTML) — escape ALL raw data placed in them.
const esc = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

// CC-49594: publish the grid's export view to the surrounding shell so it can
// offer the client-built PDF. The context provider lives inside
// SpecializedReportShell, so this wrapper must render as a child of the shell.
const PublishedGrid = ({ grouped, report, storageKey, pageStateRef, initialPageState }) => {
  const exportCtx = useReportExportView()
  const exportViewRef = useRef(null)
  useEffect(() => {
    if (!exportCtx) return
    exportCtx.ref.current = () => exportViewRef.current?.()
    exportCtx.onPublish?.()
  }, [exportCtx])
  const Grid = grouped ? ReportGroupedGrid : ReportDataGrid
  return (
    <Grid
      report={report}
      storageKey={storageKey}
      exportViewRef={exportViewRef}
      pageStateRef={pageStateRef}
      initialPageState={initialPageState}
    />
  )
}

// CC-49907: the aging buckets are distinct RANGES (ThirtyRange = 30-59 days,
// SixtyRange = 60-89, NinetyPlus = 90+). The old "30+/60+" labels read as
// overlapping cumulative buckets; V1's headers are "30-59 / 60-89 / 90+"
// (APReportHomeView.js) — match them.
// CC-50291: these columnIds are hand-written stable names, so they double as
// the firm's per-column rename keys. withAliasKeys says so explicitly.
const SUMMARY_COLUMNS = withAliasKeys([
  { columnId: 'vendor', label: 'Vendor', visible: true, columnAlignment: 'LEFT' },
  { columnId: 'current', label: 'Current', visible: true, columnAlignment: 'RIGHT' },
  { columnId: 'thirty', label: '30-59', visible: true, columnAlignment: 'RIGHT' },
  { columnId: 'sixty', label: '60-89', visible: true, columnAlignment: 'RIGHT' },
  { columnId: 'ninety', label: '90+', visible: true, columnAlignment: 'RIGHT' },
  { columnId: 'total', label: 'Total', visible: true, columnAlignment: 'RIGHT' },
])

const DETAIL_COLUMNS = withAliasKeys([
  { columnId: 'vendor', label: 'Vendor / Bill', visible: true, columnAlignment: 'LEFT' },
  { columnId: 'date', label: 'Date', visible: true, columnAlignment: 'LEFT' },
  { columnId: 'due', label: 'Due', visible: true, columnAlignment: 'LEFT' },
  // Spec 133: a day count, so "Show $ on amounts" must never turn "2845d" into
  // "$2,845.00" (reportGridUtils.neverCurrency reads this flag).
  { columnId: 'pastDue', label: 'Past Due', visible: true, columnAlignment: 'RIGHT', currency: false },
  { columnId: 'amount', label: 'Amount', visible: true, columnAlignment: 'RIGHT' },
  { columnId: 'total', label: 'Total', visible: true, columnAlignment: 'RIGHT' },
])

// Note the mixed casing — `vendorId`/`vendorName` are camelCase but `AsOfDate` is
// Pascal, which is what the filter class declares. The specs are the only place
// those names appear now and the wire test pins them.
const FIELDS = [
  objectField({ key: 'vendor', label: 'Vendor', typeId: TYPE_VENDORS, idKey: 'vendorId', nameKey: 'vendorName' }),
  dateField({ key: 'asOf', label: 'As Of', valueKey: 'AsOfDate' }),
]

const makeDefaults = () => ({
  vendorId: null,
  vendorName: '',
  AsOfDate: dayjs().format('YYYY-MM-DD'),
})

// `agedSection: 0` (Total) has no control and must survive every edit.
const toWire = (f) => ({
  vendorId: f.vendorId,
  vendorName: f.vendorName,
  AsOfDate: f.AsOfDate,
  agedSection: 0, // Total
})

// CC-50148: reportId/jobId were always PASSED by ReportView; this page just never
// declared them, which is why it had no queue control and no way to reopen a saved
// run. The queue spec is withheld without a reportId (a job record requires one).
const APReport = ({ reportId, name = 'Accounts Payable', summary, isDetailDefault = false, jobId }) => {
  const [filters, setFilters] = useState(makeDefaults)
  // Summary/detail toggle stays page-level (two different payloads + grids).
  const [mode, setMode] = useState(isDetailDefault ? 'detail' : 'summary')
  // Spec 131: only the NEWEST run may write the rows, the spinner, the error or
  // the documents' criteria — Summary/Detail re-runs, and neither button is
  // disabled while a run is out. Spinner immediately when reopening.
  const {
    running, error, ran, runKey, run: startRun, invalidate: invalidateRun,
  } = useLatestRun({ initialRunning: !!jobId, errorMessage: runErrorMessage })
  const [savedAsOf, setSavedAsOf] = useState(null)
  // Consume-once: the first run reopens the tray saved job; later runs are fresh.
  const jobRef = useRef(jobId || null)
  // CC-50013 (G3): saved views. The hook carries the grid's page state, so
  // the menu cannot be mounted without it (the CC-49732 defect).
  const view = useSavedReportView(setFilters)
  // CC-50029: exports state the period they cover.
  const filtersSummary = useMemo(() => describeFilters(dateRangePart(null, filters.AsOfDate)), [filters])
  const [summaryData, setSummaryData] = useState(null)
  const [detailData, setDetailData] = useState(null)

  const filterPayload = useMemo(() => toWire(filters), [filters])

  const runReport = useCallback(() => {
    if (!filters.AsOfDate) {
      // Spec 131: the refusal also retires a run still in flight.
      invalidateRun({ error: 'As of date is required.' })
      return undefined
    }
    return startRun(async () => {
      const jid = jobRef.current
      jobRef.current = null // consumed — subsequent runs are fresh
      if (jid) {
        // Tray "View report" → reopen the exact saved snapshot + show its run time.
        // The saved payload is whichever shape the queued run produced, so it is
        // routed by the mode the page is currently in — a summary job reopened in
        // detail mode would render an empty grid rather than the wrong numbers.
        const [st, r] = await Promise.all([reportJobService.status(jid), reportJobService.result(jid)])
        return () => {
          setSavedAsOf(st?.createdAt || null)
          if (mode === 'summary') setSummaryData(r?.data?.report || null)
          else setDetailData(r?.data?.reportDetails || null)
        }
      }
      if (mode === 'summary') {
        const data = await apReportService.getSummary(filterPayload)
        // Spec 131 decision 3: the saved-result banner clears at the COMMIT.
        return () => { setSavedAsOf(null); setSummaryData(data?.report || null) }
      }
      const data = await apReportService.getDetail(filterPayload)
      return () => { setSavedAsOf(null); setDetailData(data?.reportDetails || null) }
    }, { criteria: { filtersSummary } })
  }, [filterPayload, filters.AsOfDate, mode, startRun, invalidateRun, filtersSummary])

  // Spec 131: deferred and cancellable, so an effect pass torn down before the
  // timer fires (StrictMode's simulated remount) starts no run — the second
  // mount run would otherwise now deterministically supersede a ?jobId= reopen.
  useEffect(() => {
    const t = setTimeout(() => { runReport() }, 0)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode])

  // ----- summary view: flat vendor rows with aging buckets -----
  // CB returns APReportSummary { lineItems: APReportLine[], reportTotals }
  // (camelized from C# LineItems/ReportTotals).
  const summaryRows = useMemo(() => (
    Array.isArray(summaryData?.reportLines?.lineItems)
      ? summaryData.reportLines.lineItems
      : Array.isArray(summaryData?.reportLines)
        ? summaryData.reportLines
        : []
  ), [summaryData])

  const summaryReport = useMemo(() => {
    const reportData = summaryRows.map((r) => ({
      isGroup: false,
      dataCells: [
        { columnId: 'vendor', displayValue: esc(r.name) },
        { columnId: 'current', displayValue: fmtMoney(r.current), dataValue: Number(r.current || 0) },
        { columnId: 'thirty', displayValue: fmtMoney(r.thirtyRange), dataValue: Number(r.thirtyRange || 0) },
        { columnId: 'sixty', displayValue: fmtMoney(r.sixtyRange), dataValue: Number(r.sixtyRange || 0) },
        {
          columnId: 'ninety',
          // Red risk formatting preserved: 90+ balances render as an alert.
          displayValue: Number(r.ninetyPlus) > 0
            ? `<span class="text-red-600 font-medium">${fmtMoney(r.ninetyPlus)}</span>`
            : fmtMoney(r.ninetyPlus),
          dataValue: Number(r.ninetyPlus || 0),
        },
        { columnId: 'total', displayValue: fmtMoney(r.total), dataValue: Number(r.total || 0) },
      ],
    }))
    // SUM-only totals: plain additions over the additive vendor line items
    // (identical math to the old bespoke footer) — never averaged/weighted.
    const sum = (k) => summaryRows.reduce((acc, r) => acc + Number(r[k] || 0), 0)
    const reportFooterCells = summaryRows.length > 0
      ? [
          { columnId: 'vendor', displayValue: `Total (${summaryRows.length})` },
          { columnId: 'current', displayValue: fmtMoney(sum('current')) },
          { columnId: 'thirty', displayValue: fmtMoney(sum('thirtyRange')) },
          { columnId: 'sixty', displayValue: fmtMoney(sum('sixtyRange')) },
          { columnId: 'ninety', displayValue: fmtMoney(sum('ninetyPlus')) },
          { columnId: 'total', displayValue: fmtMoney(sum('total')) },
        ]
      : []
    return { columns: SUMMARY_COLUMNS, reportData, reportFooterCells }
  }, [summaryRows])

  // ----- detail view: aged sections -> grouped grid (server order preserved) -----
  const detailSections = useMemo(
    () => (Array.isArray(detailData?.agedSections) ? detailData.agedSections : []),
    [detailData]
  )
  const reportTotals = detailData?.reportTotals || {}

  const detailReport = useMemo(() => {
    const reportData = detailSections.map((sec) => {
      const sectionTotal = sec.sectionTotal || {}
      const lineItems = sec.lineItems || []
      return {
        isGroup: true,
        groupHeaderContent: `${esc(sec.sectionName)} <span class="text-xs font-normal text-gray-500">(${lineItems.length})</span>`,
        dataRows: lineItems.map((li) => ({
          isGroup: false,
          dataCells: [
            {
              columnId: 'vendor',
              // Two-line vendor/transaction cell (data itself escaped).
              displayValue: `<div class="font-medium">${esc(li.vendorName)}</div><div class="text-xs text-gray-500">${esc(li.transactionName)}</div>`,
            },
            { columnId: 'date', displayValue: esc(fmtDisplayDate(li.transactionDate)) },
            { columnId: 'due', displayValue: esc(fmtDisplayDate(li.dueDate)) },
            {
              columnId: 'pastDue',
              // Red risk badge preserved: > 60 days past due.
              displayValue: Number(li.pastDue) > 60
                ? `<span class="text-red-600 font-medium">${Number(li.pastDue)}d</span>`
                : (Number(li.pastDue) > 0 ? `${Number(li.pastDue)}d` : ''),
              // Spec 133: a value only where the screen shows one. The 7 vendor
              // credits on the live run carry pastDue 0 and a BLANK cell, but the
              // cell used to carry dataValue 0 as well, so every client export
              // printed "0" days past due under a blank screen cell. With no value
              // the export writes the screen's blank; a 0 adds nothing to any total.
              ...(Number(li.pastDue) > 0 ? { dataValue: Number(li.pastDue) } : {}),
            },
            { columnId: 'amount', displayValue: fmtMoney(li.amount), dataValue: Number(li.amount || 0) },
            { columnId: 'total', displayValue: fmtMoney(li.total), dataValue: Number(li.total || 0) },
          ],
        })),
        // Server-provided section totals pass through untouched.
        groupFooterCells: [
          { columnId: 'vendor', displayValue: `Total ${esc(sec.sectionName)}` },
          { columnId: 'amount', displayValue: fmtMoney(sectionTotal.amount) },
          { columnId: 'total', displayValue: fmtMoney(sectionTotal.total) },
        ],
      }
    })
    const reportFooterCells = detailSections.length > 0
      ? [
          { columnId: 'vendor', displayValue: 'Report Total' },
          { columnId: 'amount', displayValue: fmtMoney(reportTotals.amount) },
          { columnId: 'total', displayValue: fmtMoney(reportTotals.total) },
        ]
      : []
    return { columns: DETAIL_COLUMNS, reportData, reportFooterCells }
  }, [detailSections, reportTotals])

  // CC-50013 G2 / CC-50148: shared metadata-driven bar (see FIELDS / toWire).
  // The Summary/Detail switch is NOT a filter — it selects a different endpoint,
  // payload and grid, and triggers its own re-run — so it stays a page control in
  // the action row rather than becoming a filter field.
  const filterBar = (
    <SpecializedFilterBar
      fields={FIELDS}
      filters={filters}
      onFiltersChange={setFilters}
      defaults={makeDefaults()}
      onRun={runReport}
      isLoading={running}
      /* CC-50148: now that the page declares reportId/jobId (and can reopen a
         saved result), the queue is safe to offer. The actionKey and path follow
         the MODE, so a summary job and a detail job are separate cache entries —
         they are different payloads rendered by different grids, and sharing a
         dedupe key between them would hand one mode the other's result. */
      queue={reportId ? {
        reportId,
        name,
        actionKey: mode === 'detail' ? 'ap-detail' : 'ap-summary',
        endpoint: (f) => ({
          method: 'POST',
          path: mode === 'detail' ? '/reports/ap/detail' : '/reports/ap/summary',
          body: toWire(f),
        }),
        // Spec 202: detail emails its own file; summary has none, so it stays a link.
        attachFormats: specializedAttachFormats(mode === 'detail' ? '/reports/ap/detail' : '/reports/ap/summary'),
      } : undefined}
      actions={(
        <>
          <span className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
            View
            <span className="flex gap-1 bg-gray-100 rounded-lg p-1">
              {[['summary', 'Summary'], ['detail', 'Detail']].map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  aria-pressed={mode === id}
                  onClick={() => setMode(id)}
                  className={`px-3 py-1 rounded text-sm font-medium normal-case ${
                    mode === id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
                  }`}
                >
                  {label}
                </button>
              ))}
            </span>
          </span>
          {/* Summary and detail are different payloads rendered by different
              grids under different storage keys, so they get separate view sets —
              a layout saved against the summary columns is meaningless in detail
              mode. Keying the menu on `mode` scopes each saved view to the shape
              it was captured from. */}
          <ReportViewsMenu
            key={mode}
            storageKey={mode === 'detail' ? 'report:ap-detail' : 'report:ap-summary'}
            currentFilters={filters}
            onApply={view.applyView}
            getPageState={view.getPageState}
          />
        </>
      )}
    />
  )

  return (
    <SpecializedReportShell
      name={name} summary={summary} running={running} error={error}
      ranCriteria={ran?.criteria ?? null} runKey={runKey}
      filterBar={filterBar} filtersSummary={filtersSummary} savedAsOf={savedAsOf}
      // Detail mode keeps the authoritative SERVER export; summary mode has no
      // server endpoint, so the shell's client-built CSV/Excel (from the
      // published grid view) covers it — CC-49908. Filenames: V1's
      // "{ts}_{Report_Name}.{ext}" convention (CC-49852).
      download={mode === 'detail'
        ? (format, getToken) => apReportService.download(filterPayload, format, getToken, exportFileName(name, format))
        : undefined}
    >
      {mode === 'summary' ? (
        <PublishedGrid key={view.gridKey} report={summaryReport} storageKey="report:ap-summary" pageStateRef={view.pageStateRef} initialPageState={view.initialPageState} />
      ) : (
        <PublishedGrid key={view.gridKey} grouped report={detailReport} storageKey="report:ap-detail" pageStateRef={view.pageStateRef} initialPageState={view.initialPageState} />
      )}
    </SpecializedReportShell>
  )
}

export const APDetailReport = (props) => <APReport {...props} isDetailDefault name={props.name || 'Accounts Payable Detail'} />

export default APReport
